import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Upload, FileText, Loader2, CheckCircle2, XCircle, BarChart3,
  Clock, Globe, Bot, Download, Trash2, ChevronDown, ChevronRight,
  Search, Lock, Eye, Activity, Chrome, ArrowRight, Server
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { verifyApi } from '@/lib/verifyApi';

// === Upload Step ===
function UploadStep({ onComplete }) {
  const [file, setFile] = useState(null);
  const [domain, setDomain] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file || !domain.trim()) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('site_domain', domain.trim());
      fd.append('format_hint', 'auto');
      fd.append('bytes_estimate', String(file.size));
      const deviceId = localStorage.getItem('ac_device_id') || '';
      if (deviceId) fd.append('device_id', deviceId);
      const result = await verifyApi.uploadLog(fd, setProgress);
      toast.success('Log uploaded successfully');
      // Immediately start job
      const job = await verifyApi.createJob(result.upload_id);
      onComplete({ uploadId: result.upload_id, jobId: job.job_id, upload: result });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
      setUploading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="upload-step">
      <div
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
          file ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
        }`}
        data-testid="upload-dropzone"
      >
        <input ref={fileRef} type="file" accept=".log,.txt,.gz" className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input" />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <FileText className="w-8 h-8 text-emerald-600" />
            <div className="text-left">
              <p className="font-medium text-slate-900">{file.name}</p>
              <p className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
            </div>
          </div>
        ) : (
          <>
            <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700">Drop or click to upload access log</p>
            <p className="text-xs text-slate-400 mt-1">.log, .txt, .gz — Nginx or Apache format</p>
          </>
        )}
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600 mb-1.5 block">Site domain</label>
        <Input data-testid="domain-input" value={domain} onChange={(e) => setDomain(e.target.value)}
          placeholder="example.com" className="h-11 rounded-xl" />
      </div>

      <div className="bg-slate-50 rounded-xl p-4 space-y-2">
        <p className="text-xs font-semibold text-slate-600">Privacy defaults (on by default)</p>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Query params stripped
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Raw log lines not stored
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Delete anytime
        </div>
      </div>

      {uploading && (
        <div className="space-y-2" data-testid="upload-progress">
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#007AFF] transition-all duration-300 rounded-full" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-slate-500 text-center">{progress}% uploaded</p>
        </div>
      )}

      <Button onClick={handleUpload} disabled={!file || !domain.trim() || uploading}
        data-testid="upload-btn"
        className="w-full h-12 rounded-xl bg-[#007AFF] hover:bg-[#0062CC] text-white font-medium">
        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Upload & Analyze'}
      </Button>
    </div>
  );
}

// === Job Progress Step ===
function JobStep({ jobId, onComplete }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let interval = 1200;

    const poll = async () => {
      try {
        const j = await verifyApi.getJob(jobId);
        if (cancelled) return;
        setJob(j);
        if (j.status === 'done' && j.report_id) {
          onComplete(j.report_id);
          return;
        }
        if (j.status === 'failed') {
          setError(j.error || 'Analysis failed');
          return;
        }
        interval = Math.min(3000, interval + 100);
        setTimeout(poll, interval);
      } catch (err) {
        console.warn('Job poll error:', err);
        if (!cancelled) setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [jobId, onComplete]);

  const pct = job?.progress?.pct || 0;
  const lines = job?.progress?.lines_read || 0;

  return (
    <div className="max-w-md mx-auto text-center space-y-6 py-8" data-testid="job-step">
      {error ? (
        <>
          <XCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h3 className="text-lg font-semibold text-slate-900">Analysis Failed</h3>
          <p className="text-sm text-slate-500">{error}</p>
        </>
      ) : (
        <>
          <div className="relative w-20 h-20 mx-auto">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke="#E2E8F0" strokeWidth="6" />
              <circle cx="40" cy="40" r="34" fill="none" stroke="#007AFF" strokeWidth="6"
                strokeDasharray={`${pct * 2.14} 214`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-[#007AFF]">{pct}%</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Analyzing logs...</h3>
            <p className="text-sm text-slate-500 mt-1">{lines.toLocaleString()} lines parsed</p>
          </div>
          <div className="flex justify-center gap-6 text-xs text-slate-400">
            <span>Parse</span><ArrowRight className="w-3 h-3" /><span>Classify</span><ArrowRight className="w-3 h-3" /><span>Aggregate</span>
          </div>
        </>
      )}
    </div>
  );
}

// === Report Dashboard ===
function ReportDashboard({ reportId }) {
  const [report, setReport] = useState(null);
  const [bots, setBots] = useState([]);
  const [timeseries, setTimeseries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      verifyApi.getReport(reportId).then(setReport),
      verifyApi.getReportBots(reportId).then(d => setBots(d.bots || [])),
      verifyApi.getTimeseries(reportId).then(d => setTimeseries(d.points || [])),
    ]).catch(console.error).finally(() => setLoading(false));
  }, [reportId]);

  const handleExport = async () => {
    const data = await verifyApi.exportReport(reportId);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `verify-report-${reportId}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this report and uploaded log?')) return;
    await verifyApi.deleteReport(reportId);
    if (report?.upload_id) await verifyApi.deleteUpload(report.upload_id).catch(() => {});
    toast.success('Report deleted');
    window.location.href = '/verify';
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#007AFF] animate-spin" /></div>;
  if (!report) return <div className="text-center py-16 text-slate-400">Report not found</div>;

  const s = report.summary;
  const ai = report.ai_bot_activity;
  const conf = report.confidence_model;

  // Simple timeseries bar chart
  const maxReqs = Math.max(...timeseries.map(p => p.total_requests), 1);

  return (
    <div className="space-y-8" data-testid="report-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
              Verified Activity
            </span>
            <span className="text-xs text-slate-400">{report.site_domain}</span>
          </div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="report-title">
            Bot Activity Report
          </h2>
          {s.time_range?.start && (
            <p className="text-xs text-slate-400 mt-0.5">
              {new Date(s.time_range.start).toLocaleDateString()} — {new Date(s.time_range.end).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-xs" onClick={handleExport} data-testid="export-btn">
            <Download className="w-3.5 h-3.5" /> Export JSON
          </Button>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-xs text-red-600 hover:text-red-700" onClick={handleDelete} data-testid="delete-btn">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="kpi-grid">
        <KpiCard label="Total Requests" value={s.total_requests?.toLocaleString()} icon={Activity} />
        <KpiCard label="Unique IPs" value={s.unique_ips?.toLocaleString()} icon={Globe} />
        <KpiCard label="Bot Requests" value={ai.requests_from_bots?.toLocaleString()} icon={Bot} color="text-purple-600" />
        <KpiCard label="Bots Detected" value={ai.bots_detected} icon={Search} color="text-red-600" />
        <KpiCard label="Confidence" value={`${conf.overall_score}/100`} icon={CheckCircle2} color="text-emerald-600" />
      </div>

      {/* Bot traffic % */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-slate-900">Bot Traffic</span>
          <span className="text-2xl font-bold text-purple-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{ai.pct_bot_traffic}%</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${Math.min(100, ai.pct_bot_traffic)}%` }} />
        </div>
        <p className="text-xs text-slate-400 mt-2">{ai.requests_from_bots?.toLocaleString()} of {s.total_requests?.toLocaleString()} requests from detected bots</p>
      </div>

      {/* Timeseries chart */}
      {timeseries.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Bot Activity Over Time</h3>
          <div className="flex items-end gap-1 h-32" data-testid="timeseries-chart">
            {timeseries.map((p, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${p.ts?.slice(0,10)}: ${p.bot_requests} bot / ${p.total_requests} total`}>
                <div className="w-full rounded-t" style={{ height: `${(p.total_requests / maxReqs) * 100}%`, minHeight: 2, backgroundColor: '#E2E8F0' }}>
                  <div className="w-full rounded-t bg-purple-500" style={{ height: `${p.total_requests > 0 ? (p.bot_requests / p.total_requests) * 100 : 0}%`, minHeight: p.bot_requests > 0 ? 2 : 0 }} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-slate-400">
            <span>{timeseries[0]?.ts?.slice(0,10)}</span>
            <span>{timeseries[timeseries.length-1]?.ts?.slice(0,10)}</span>
          </div>
          <div className="flex items-center gap-4 mt-2 text-[10px]">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-slate-200" /> Total</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-purple-500" /> Bot</span>
          </div>
        </div>
      )}

      {/* Top Bots Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Detected Bots</h3>
        </div>
        <table className="w-full" data-testid="bots-table">
          <thead>
            <tr className="bg-slate-50 text-[11px] text-slate-500 uppercase tracking-wider">
              <th className="px-5 py-2.5 text-left">Bot</th>
              <th className="px-5 py-2.5 text-left">Org</th>
              <th className="px-5 py-2.5 text-right">Requests</th>
              <th className="px-5 py-2.5 text-right">IPs</th>
              <th className="px-5 py-2.5 text-right">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {bots.map((b) => (
              <tr key={b.bot_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`bot-row-${b.bot_id}`}>
                <td className="px-5 py-3">
                  <span className="font-medium text-sm text-slate-900">{b.bot_name}</span>
                  {b.tier === 1 && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">T1</span>}
                  {b.tier === 3 && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-semibold">T3</span>}
                </td>
                <td className="px-5 py-3 text-sm text-slate-500">{b.org}</td>
                <td className="px-5 py-3 text-sm text-right font-mono text-slate-900">{b.requests?.toLocaleString()}</td>
                <td className="px-5 py-3 text-sm text-right font-mono text-slate-500">{b.unique_ips?.toLocaleString()}</td>
                <td className="px-5 py-3 text-right">
                  <span className={`text-sm font-semibold ${b.confidence >= 80 ? 'text-emerald-600' : b.confidence >= 50 ? 'text-amber-600' : 'text-slate-400'}`}>
                    {b.confidence}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Top Paths */}
      {s.top_paths?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Top Paths Hit</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {s.top_paths.slice(0, 10).map((p, i) => (
              <div key={i} className="px-5 py-2.5 flex items-center justify-between">
                <span className="text-sm font-mono text-slate-700 truncate max-w-[70%]">{p.path}</span>
                <span className="text-sm font-mono text-slate-400">{p.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Codes */}
      {s.status_counts && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Status Codes</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(s.status_counts).sort((a,b) => b[1] - a[1]).map(([code, count]) => (
              <div key={code} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                <span className={`text-lg font-bold font-mono ${
                  code.startsWith('2') ? 'text-emerald-600' : code.startsWith('4') ? 'text-red-600' : 'text-amber-600'
                }`}>{code}</span>
                <p className="text-[10px] text-slate-400 mt-0.5">{count.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Methodology */}
      <p className="text-xs text-slate-400 text-center">
        Verified from uploaded server logs (User-Agent classification). Catalog: {report.bot_catalog_version}. {conf.notes?.[0]}
      </p>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color = 'text-slate-900' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-xl font-bold ${color}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{value}</p>
    </div>
  );
}

// === Main Page ===
export default function VerifyPage() {
  const [step, setStep] = useState('upload'); // upload | job | report
  const [jobId, setJobId] = useState(null);
  const [reportId, setReportId] = useState(null);

  const handleUploadComplete = ({ jobId: jid }) => {
    setJobId(jid);
    setStep('job');
  };

  const handleJobComplete = useCallback((rid) => {
    setReportId(rid);
    setStep('report');
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="verify-page">
      {/* Header */}
      <header className="border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AgentCheck</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">Scan</Link>
              <Link to="/leaderboard/ai-bots" className="text-sm text-slate-600 hover:text-slate-900">Leaderboard</Link>
              <Link to="/pricing" className="text-sm text-[#007AFF] hover:text-[#0062CC] font-medium">Pro</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero (only on upload step) */}
      {step === 'upload' && (
        <section className="bg-slate-50 border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 mb-4">
              <Server className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-700">Verified Activity</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Verify AI Bot Activity
            </h1>
            <p className="text-sm text-slate-500 mt-2 max-w-lg mx-auto">
              Upload server access logs to see which AI bots are actually visiting your site.
              User-Agent + IP/ASN + behavior analysis.
            </p>
            <div className="flex items-center justify-center gap-6 mt-4 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Redaction on by default</span>
              <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> No raw lines stored</span>
              <span className="flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete anytime</span>
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {step === 'upload' && <UploadStep onComplete={handleUploadComplete} />}
        {step === 'job' && <JobStep jobId={jobId} onComplete={handleJobComplete} />}
        {step === 'report' && reportId && <ReportDashboard reportId={reportId} />}
      </div>
    </div>
  );
}
